import { useState, useEffect } from "react";
import { Gift, X } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import announcementService from "../Services/announcementService";
import authService from "../Services/authService";
import useCoinStore from "../stores/coinStore";

// Dismissed announcements stay dismissed on this device, instead of coming
// back on every visit to Home.
const DISMISSED_KEY = "nameit.dismissedAnnouncements";
const readDismissed = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]");
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
};

const AnnouncementToast = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    loadBanner();
  }, []);

  useEffect(() => {
    if (announcement && !dismissed) {
      const timer = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [announcement, dismissed]);

  const loadBanner = async () => {
    const active = await announcementService.getActiveAnnouncements();
    if (active.length === 0) return;

    const dismissedIds = readDismissed();
    const first = active.find((a) => !dismissedIds.includes(a.id));
    if (!first) return;
    setAnnouncement(first);

    const user = authService.auth.currentUser;
    if (user && !user.isAnonymous && first.coinReward > 0) {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const claimed = userDoc.data().claimedAnnouncements || {};
          if (claimed[first.id]) setClaimed(true);
        }
      } catch (e) {
        console.error("Failed to check claim status:", e);
      }
    }
  };

  const handleClaim = async () => {
    if (!announcement || claiming) return;
    setClaiming(true);
    try {
      await announcementService.claimReward(announcement.id, announcement.coinReward);
      useCoinStore.setState((s) => ({ coinBalance: s.coinBalance + announcement.coinReward }));
      setClaimed(true);
      setShowFlash(true);
      setTimeout(() => setShowFlash(false), 2500);
    } catch (error) {
      console.error("Claim failed:", error);
    } finally {
      setClaiming(false);
    }
  };

  const handleDismiss = () => {
    try {
      const ids = readDismissed().filter((id) => id !== announcement.id).slice(-19);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids, announcement.id]));
    } catch {
      /* storage unavailable: it just shows again next visit */
    }
    setVisible(false);
    setTimeout(() => setDismissed(true), 300);
  };

  if (!announcement || dismissed) return null;

  const user = authService.auth.currentUser;
  const isSignedIn = !!(user && !user.isAnonymous);
  const hasReward = announcement.coinReward > 0;
  const bgColor = announcement.bgColor || "#f97316";

  return (
    <div
      className={`fixed top-16 left-4 right-4 z-50 transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
      }`}
    >
      <div
        className="rounded-2xl shadow-lg p-4 text-white relative"
        style={{ backgroundColor: showFlash ? "#16a34a" : bgColor }}
      >
        <button
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          className="absolute top-1 right-1 w-11 h-11 flex items-center justify-center hover:bg-white/20 rounded-full transition"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 pr-6">
          {announcement.emoji && (
            <span className="text-xl flex-shrink-0">{announcement.emoji}</span>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{announcement.title}</p>
            {announcement.message && (
              <p className="text-white/80 text-xs mt-0.5">{announcement.message}</p>
            )}
          </div>
        </div>

        {hasReward && (
          <div className="mt-3 flex justify-end">
            {showFlash ? (
              <span className="bg-white/25 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                +{announcement.coinReward} coins added!
              </span>
            ) : claimed ? (
              <span className="bg-white/20 text-white/80 text-xs font-medium px-3 py-1.5 rounded-full">
                Claimed
              </span>
            ) : isSignedIn ? (
              <button
                onClick={handleClaim}
                disabled={claiming}
                className="bg-white text-gray-900 text-xs font-bold px-4 py-2 rounded-full hover:bg-gray-100 active:scale-95 transition-all disabled:opacity-60 flex items-center gap-1.5"
              >
                <Gift size={12} />
                {claiming ? "Claiming..." : `Claim ${announcement.coinReward} coins`}
              </button>
            ) : (
              <span className="bg-white/15 text-white/80 text-xs px-3 py-1.5 rounded-full border border-white/20">
                Sign in to claim
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementToast;
